import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { AclFeature, AclPermission, RoleAclMap } from '@/mocks/aclPermissions';
import { defaultAcl, hasPermission as checkPermission } from '@/mocks/aclPermissions';

export type SystemRole = string;

interface AclContextValue {
  role: SystemRole;
  acl: RoleAclMap;
  can: (feature: AclFeature, permission: AclPermission) => boolean;
  canView: (feature: AclFeature) => boolean;
  canManage: (feature: AclFeature) => boolean;
  updateAcl: (newAcl: RoleAclMap) => void;
  updateRolePermissions: (roleName: string, feature: AclFeature, permissions: AclPermission[]) => void;
  setRole: (role: SystemRole) => void;
}

const AclContext = createContext<AclContextValue | null>(null);

const ROLE_STORAGE_KEY = 'xl-dess-sis.activeRole';


function readStoredRole(fallbackRole: SystemRole): SystemRole {
  try {
    return window.localStorage.getItem(ROLE_STORAGE_KEY) ?? fallbackRole;
  } catch {
    return fallbackRole;
  }
}

function persistRole(role: SystemRole): void {
  try {
    window.localStorage.setItem(ROLE_STORAGE_KEY, role);
  } catch {
    // Non-fatal: the role stays correct for this session, just not across reloads.
  }
}

export function AclProvider({ children, initialRole = 'Super Admin', initialAcl }: {
  children: ReactNode;
  initialRole?: SystemRole;
  initialAcl?: RoleAclMap;
}) {
  const [role, setRoleState] = useState<SystemRole>(() => readStoredRole(initialRole));
  const [acl, setAcl] = useState<RoleAclMap>(initialAcl ?? defaultAcl);

  const can = useCallback(
    (feature: AclFeature, permission: AclPermission) => checkPermission(acl, role, feature, permission),
    [acl, role],
  );

  const canView = useCallback(
    (feature: AclFeature) => checkPermission(acl, role, feature, 'view'),
    [acl, role],
  );

  const canManage = useCallback(
    (feature: AclFeature) => checkPermission(acl, role, feature, 'manage'),
    [acl, role],
  );

  const updateAcl = useCallback((newAcl: RoleAclMap) => {
    setAcl(newAcl);
  }, []);

  const updateRolePermissions = useCallback(
    (roleName: string, feature: AclFeature, permissions: AclPermission[]) => {
      setAcl((prev) => ({
        ...prev,
        [roleName]: {
          ...prev[roleName],
          [feature]: permissions,
        },
      }));
    },
    [],
  );

  const setRole = useCallback((newRole: SystemRole) => {
    setRoleState(newRole);
    persistRole(newRole);
  }, []);

  return (
    <AclContext.Provider value={{ role, acl, can, canView, canManage, updateAcl, updateRolePermissions, setRole }}>
      {children}
    </AclContext.Provider>
  );
}

export function useAcl(): AclContextValue {
  const ctx = useContext(AclContext);
  if (!ctx) {
    throw new Error('useAcl must be used within an AclProvider');
  }
  return ctx;
}